import axios from "axios";

// 1. Get ONLY the server address from environment variables.
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000";
// Circuit breaker state - less aggressive settings
let circuitBreaker = {
  failures: 0,
  lastFailureTime: 0,
  state: 'CLOSED', // CLOSED, OPEN, HALF_OPEN
  failureThreshold: 10, // Increased from 5 to 10
  recoveryTimeout: 30000, // Reduced from 60s to 30s
};

// 2. Create the Axios instance.
const api = axios.create({
  baseURL: API_BASE_URL, // This will be 'http://localhost:5000' or 'http://13.200.50.80'
  timeout: 30000, // 30 second timeout
});

// 3. Updated interceptor to include admin-only routes like /submissions and /points.
api.interceptors.request.use(
  (config) => {
    const isAdminRoute =
      config.url.includes("/admin/") ||
      config.url.includes("/auth/admin/") ||
      config.url.includes("/submissions/") ||
      config.url.includes("/points/") ||
      config.url.includes("/users/management/") || // Add role management endpoint
      config.url.includes("/teams/"); // Add teams management routes

    const token = isAdminRoute
      ? localStorage.getItem("adminToken")
      : localStorage.getItem("userToken");

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Circuit breaker function
const canMakeRequest = () => {
  const now = Date.now();

  if (circuitBreaker.state === 'OPEN') {
    if (now - circuitBreaker.lastFailureTime > circuitBreaker.recoveryTimeout) {
      circuitBreaker.state = 'HALF_OPEN';
      return true;
    }
    return false;
  }

  return true;
};

const recordFailure = () => {
  circuitBreaker.failures++;
  circuitBreaker.lastFailureTime = Date.now();

  if (circuitBreaker.failures >= circuitBreaker.failureThreshold) {
    circuitBreaker.state = 'OPEN';
    console.warn('Circuit breaker opened - stopping requests to prevent overload');
  }
};

const recordSuccess = () => {
  if (circuitBreaker.state === 'HALF_OPEN') {
    circuitBreaker.state = 'CLOSED';
    circuitBreaker.failures = 0;
    console.log('Circuit breaker closed - service recovered');
  }
};

// Request interceptor with circuit breaker
api.interceptors.request.use(
  (config) => {
    // Check circuit breaker before making request
    if (!canMakeRequest()) {
      return Promise.reject(new Error('Circuit breaker is OPEN - service temporarily unavailable'));
    }

    const isAdminRoute =
      config.url.includes("/admin/") ||
      config.url.includes("/auth/admin/") ||
      config.url.includes("/submissions/") ||
      config.url.includes("/points/") ||
      config.url.includes("/users/management/") || // Add role management endpoint
      config.url.includes("/teams/"); // Add teams management routes

    const token = isAdminRoute
      ? localStorage.getItem("adminToken")
      : localStorage.getItem("userToken");

    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor with circuit breaker and token expiry handling
api.interceptors.response.use(
  (response) => {
    // Record success for circuit breaker
    recordSuccess();
    return response;
  },
  (error) => {
    // Handle token expiry (401 Unauthorized) more intelligently
    if (error.response && error.response.status === 401) {
      // Don't redirect for login/auth endpoints - these are expected 401s for invalid credentials
      const isLoginEndpoint = error.config.url.includes('/auth/login') ||
                             error.config.url.includes('/auth/admin/login') ||
                             error.config.url.includes('/auth/verify-otp') ||
                             error.config.url.includes('/auth/verify-gmail-otp');

      if (!isLoginEndpoint) {
        const errorMessage = error.response.data?.message?.toLowerCase();
        const errorCode = error.response.data?.code;

        // Clear tokens on explicit token/session expiry, not on other auth errors
        const shouldClearTokens = 
          errorCode === 'TOKEN_EXPIRED' ||
          (errorMessage && (
            errorMessage.includes('token failed') ||
            errorMessage.includes('token expired') ||
            errorMessage.includes('session expired') ||
            errorMessage.includes('jwt expired')
          ));

        if (shouldClearTokens) {
          // Store session expiry message to show on login page
          const expiryMessage = "Your session has expired due to inactivity. Please login again.";
          localStorage.setItem('sessionExpiredMessage', expiryMessage);
          
          // Clear tokens intelligently based on which token was used for the request
          const isAdminRoute = error.config.url.includes("/admin/") ||
                              error.config.url.includes("/auth/admin/") ||
                              error.config.url.includes("/submissions/") ||
                              error.config.url.includes("/points/") ||
                              error.config.url.includes("/users/management/") ||
                              error.config.url.includes("/teams/");

          // Only clear the relevant token based on the route type
          if (isAdminRoute) {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminRole');
            localStorage.removeItem('adminName');
            window.location.href = '/admin-login';
          } else {
            localStorage.removeItem('userToken');
            localStorage.removeItem('userRole');
            localStorage.removeItem('name');
            window.location.href = '/user-login';
          }
        }
        // For other 401 errors (like user not found), just reject - don't log out
        return Promise.reject(error);
      }
    }

    // Only count server errors and timeouts as circuit breaker failures
    // Don't count authentication errors (401), validation errors (400), or not found (404)
    const shouldTriggerCircuitBreaker =
      !error.response || // Network error/timeout
      error.response.status >= 500 || // Server errors
      error.code === 'ECONNABORTED'; // Timeout

    if (shouldTriggerCircuitBreaker) {
      recordFailure();
    }

    // If circuit breaker is open, provide user-friendly message
    if (circuitBreaker.state === 'OPEN') {
      error.message = 'Service is temporarily overloaded. Please try again in a moment.';
    }

    return Promise.reject(error);
  }
);

// Add isCancel method to the api instance
api.isCancel = axios.isCancel;

export default api;
